import { useEffect, useState } from 'react';
import { CustomerSearchCombobox } from '@/components/shared/customer-search-combobox';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { DatePicker } from '@/components/ui/date-picker';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Sheet, SheetContent, SheetDescription, SheetFooter, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Textarea } from '@/components/ui/textarea';
import type { CreatePromiseFormCustomer } from '@/hooks/useCreatePromiseData';
import { useUserContext } from '@/hooks/useUserContext';
import { useCompanyUsersList } from '@/hooks/useUsersData';
import { cn } from '@/lib/utils';
import type { TaskType } from '@/types/tasks';

// ---------------------------------------------------------------------------
// Validation constants (aligned with the backend / database schema)
// ---------------------------------------------------------------------------
const MAX_TITLE_LENGTH = 150;
const MAX_DESCRIPTION_LENGTH = 2000;

// ---------------------------------------------------------------------------
// Public types
// ---------------------------------------------------------------------------

export interface CreateTaskCustomer {
  id: string;
  accountNumber: string;
  name: string;
}

export interface NewTaskData {
  title: string;
  description: string;
  type: TaskType;
  assignedToId: string;
  assignedToName: string;
  dueDate: Date;
  customerId: string;
  customerName: string;
}

const TASK_TYPE_LABELS: Record<TaskType, string> = {
  task: 'Task',
  customer_action: 'Customer',
  transaction: 'Transaction',
  collection_case: 'Collections',
  promise: 'Promises',
  dispute: 'Disputes',
};

interface CreateTaskDrawerProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly onCreateTask: (task: NewTaskData) => void;
  /** Whether the mutation is currently in-flight */
  readonly isSubmitting?: boolean;
  /** Customer the task is raised against; locked when supplied (contextual creation) */
  readonly prefilledCustomer?: CreateTaskCustomer;
  /** Task type of the source screen; locked when supplied. Defaults to a manual task. */
  readonly taskType?: TaskType;
  readonly defaultTitle?: string;
  readonly defaultDescription?: string;
}

// ---------------------------------------------------------------------------
// Internal form state
// ---------------------------------------------------------------------------
interface FormState {
  title: string;
  description: string;
  dueDate: Date | undefined;
  customerId: string;
}

const INITIAL_FORM_STATE: FormState = {
  title: '',
  description: '',
  dueDate: undefined,
  customerId: '',
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function CreateTaskDrawer({
  open,
  onOpenChange,
  onCreateTask,
  isSubmitting = false,
  prefilledCustomer,
  taskType = 'task',
  defaultTitle,
  defaultDescription,
}: CreateTaskDrawerProps) {
  const [formData, setFormData] = useState<FormState>(INITIAL_FORM_STATE);
  const [selectedCustomer, setSelectedCustomer] = useState<CreatePromiseFormCustomer | null>(null);
  const [selectedAssigneeId, setSelectedAssigneeId] = useState<string>('');
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});

  const { data: companyUsers = [] } = useCompanyUsersList();
  const { data: userContext } = useUserContext();

  // Reinitialise from the source screen's context each time the drawer opens
  useEffect(() => {
    if (!open) return;

    setFormData({
      title: defaultTitle ?? '',
      description: defaultDescription ?? '',
      dueDate: undefined,
      customerId: prefilledCustomer?.id ?? '',
    });
    setValidationErrors({});
  }, [open, defaultTitle, defaultDescription, prefilledCustomer?.id]);

  useEffect(() => {
    if (!open) return;

    const assignedUserId = userContext?.internalUserId;
    setSelectedAssigneeId(assignedUserId ? String(assignedUserId) : '');
  }, [open, userContext?.internalUserId]);

  // ------ Validation ------
  const validate = (): boolean => {
    const errors: Record<string, string> = {};

    if (!formData.title.trim()) {
      errors.title = 'Title is required.';
    } else if (formData.title.trim().length > MAX_TITLE_LENGTH) {
      errors.title = `Title must not exceed ${MAX_TITLE_LENGTH} characters.`;
    }

    if (formData.description.length > MAX_DESCRIPTION_LENGTH) {
      errors.description = `Description must not exceed ${MAX_DESCRIPTION_LENGTH} characters.`;
    }

    if (!selectedAssigneeId) {
      errors.assignedToId = 'Assigned To is required.';
    }

    if (!formData.dueDate) {
      errors.dueDate = 'Due Date is required.';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // ------ Handlers ------
  const resetForm = () => {
    setFormData(INITIAL_FORM_STATE);
    setSelectedCustomer(null);
    setSelectedAssigneeId('');
    setValidationErrors({});
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    if (!formData.dueDate) return;

    const assignee = companyUsers.find((u) => u.id === selectedAssigneeId);

    const taskData: NewTaskData = {
      title: formData.title.trim(),
      description: formData.description.trim(),
      type: taskType,
      assignedToId: selectedAssigneeId,
      assignedToName: assignee ? `${assignee.firstName} ${assignee.lastName}` : '',
      dueDate: formData.dueDate,
      customerId: prefilledCustomer?.id ?? selectedCustomer?.id ?? '',
      customerName: prefilledCustomer?.name ?? selectedCustomer?.name ?? '',
    };

    onCreateTask(taskData);
    resetForm();
    onOpenChange(false);
  };

  const handleCancel = () => {
    resetForm();
    onOpenChange(false);
  };

  // ------ Render ------
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-lg overflow-y-auto px-8">
        <form onSubmit={handleSubmit}>
          <SheetHeader>
            <SheetTitle>Create New Task</SheetTitle>
            <SheetDescription>Add a new task to track collection activities</SheetDescription>
          </SheetHeader>

          <div className="flex flex-col gap-4 py-6">
            {/* Task Title */}
            <div className="space-y-2">
              <Label htmlFor="title">
                Task Title <span className="text-red-500">*</span>
              </Label>
              <Input
                id="title"
                placeholder="Enter task title"
                value={formData.title}
                maxLength={MAX_TITLE_LENGTH}
                onChange={(e) => {
                  setFormData({ ...formData, title: e.target.value });
                  if (validationErrors.title) {
                    setValidationErrors((prev) => {
                      const next = { ...prev };
                      delete next.title;
                      return next;
                    });
                  }
                }}
                required
              />
              <div className="flex justify-between">
                {validationErrors.title && <p className="text-xs text-red-500">{validationErrors.title}</p>}
                <p
                  className={cn(
                    'text-xs text-muted-foreground ml-auto',
                    formData.title.length > MAX_TITLE_LENGTH && 'text-red-500',
                  )}
                >
                  {formData.title.length}/{MAX_TITLE_LENGTH}
                </p>
              </div>
            </div>

            {/* Description */}
            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                placeholder="Enter task description"
                value={formData.description}
                maxLength={MAX_DESCRIPTION_LENGTH}
                onChange={(e) => {
                  setFormData({ ...formData, description: e.target.value });
                  if (validationErrors.description) {
                    setValidationErrors((prev) => {
                      const next = { ...prev };
                      delete next.description;
                      return next;
                    });
                  }
                }}
                rows={4}
              />
              <div className="flex justify-between">
                {validationErrors.description && <p className="text-xs text-red-500">{validationErrors.description}</p>}
                <p
                  className={cn(
                    'text-xs text-muted-foreground ml-auto',
                    formData.description.length > MAX_DESCRIPTION_LENGTH && 'text-red-500',
                  )}
                >
                  {formData.description.length}/{MAX_DESCRIPTION_LENGTH}
                </p>
              </div>
            </div>

            {/* Two Column Layout */}
            <div className="grid grid-cols-2 gap-4">
              {/* Row 1: Task Type (Hardcoded) */}
              <div className="space-y-2">
                <Label htmlFor="type">
                  Task Type <span className="text-red-500">*</span>
                </Label>
                <Input id="type" value={TASK_TYPE_LABELS[taskType]} disabled className="bg-muted" />
              </div>

              {/* Row 1: Assigned To */}
              <div className="space-y-2">
                <Label htmlFor="assignedTo">
                  Assigned To <span className="text-red-500">*</span>
                </Label>
                <Select
                  value={selectedAssigneeId}
                  onValueChange={(value) => {
                    setSelectedAssigneeId(value);
                    if (validationErrors.assignedToId) {
                      setValidationErrors((prev) => {
                        const next = { ...prev };
                        delete next.assignedToId;
                        return next;
                      });
                    }
                  }}
                >
                  <SelectTrigger id="assignedTo">
                    <SelectValue placeholder="Select assignee" />
                  </SelectTrigger>
                  <SelectContent>
                    {companyUsers.map((user) => (
                      <SelectItem key={user.id} value={user.id}>
                        <div className="flex items-center gap-2">
                          <Avatar className="h-5 w-5">
                            <AvatarFallback className="text-[10px]">
                              {user.firstName[0]}
                              {user.lastName[0]}
                            </AvatarFallback>
                          </Avatar>
                          <span>
                            {user.firstName} {user.lastName}
                          </span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {validationErrors.assignedToId && (
                  <p className="text-xs text-red-500">{validationErrors.assignedToId}</p>
                )}
              </div>

              {/* Row 2: Due Date */}
              <div className="col-span-2 sm:col-span-1 space-y-2">
                <Label htmlFor="dueDate">
                  Due Date <span className="text-red-500">*</span>
                </Label>
                <DatePicker
                  date={formData.dueDate}
                  onDateChange={(date) => {
                    setFormData({ ...formData, dueDate: date });
                    if (validationErrors.dueDate) {
                      setValidationErrors((prev) => {
                        const next = { ...prev };
                        delete next.dueDate;
                        return next;
                      });
                    }
                  }}
                  placeholder="Select due date"
                />
                {validationErrors.dueDate && <p className="text-xs text-red-500">{validationErrors.dueDate}</p>}
              </div>

              {/* Customer - Full Width */}
              <div className="col-span-2 space-y-2">
                <Label htmlFor="customerName">Customer</Label>
                {prefilledCustomer ? (
                  <Input
                    id="customerName"
                    value={`${prefilledCustomer.name} (${prefilledCustomer.accountNumber})`}
                    disabled
                    className="bg-muted"
                  />
                ) : (
                  <CustomerSearchCombobox
                    selectedCustomer={selectedCustomer}
                    onSelectCustomer={setSelectedCustomer}
                    placeholder="Select customer..."
                  />
                )}
              </div>
            </div>
          </div>

          <SheetFooter className="flex-row gap-2">
            <Button type="button" variant="outline" onClick={handleCancel}>
              Cancel
            </Button>
            <Button type="submit" className="bg-teal-600 hover:bg-teal-700" disabled={isSubmitting}>
              {isSubmitting ? 'Creating...' : 'Create Task'}
            </Button>
          </SheetFooter>
        </form>
      </SheetContent>
    </Sheet>
  );
}
